// DEV-ONLY store impersonation.
//
// Lets a developer view any shop's reports/data by overriding the Customer ID
// the SPA talks to — without creating a per-shop `.support` account and
// signing in/out. You stay logged in as ONE account; only the store *scope*
// (customerId sent to the API + Firestore/RTDB paths) is redirected.
//
// EVERYTHING here is gated behind `import.meta.dev`. In a production build
// `import.meta.dev` is statically `false`, so the reads below constant-fold to
// no-ops and the whole feature is tree-shaken out of the bundle. It can never
// affect production.
//
// The override is applied once at boot (see stores/bootstrap.ts) so the
// customerId is baked into every client BEFORE useDataservice.init() embeds it
// into entity URL paths — switching therefore requires a reload, which the dev
// panel does for you. See app/components/dev/DevStorePanel.vue.

const DEV_STORE_KEY = "__dev_store_override";

/** True only in `nuxt dev`. Every export below hard-returns when this is false. */
export const isDevStoreBypassAvailable = (): boolean => import.meta.dev;

/**
 * The currently active store override, or `null` when none is set (or in prod).
 * Trimmed; blank/whitespace is treated as unset.
 */
export function getDevStoreOverride(): string | null {
  if (!import.meta.dev) return null;
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(DEV_STORE_KEY);
    const trimmed = raw?.trim();
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
}

/** Persist a new override. No-op in prod / SSR. Caller reloads to apply. */
export function setDevStoreOverride(customerId: string): void {
  if (!import.meta.dev) return;
  if (typeof localStorage === "undefined") return;
  const trimmed = customerId?.trim();
  try {
    if (trimmed) localStorage.setItem(DEV_STORE_KEY, trimmed);
    else localStorage.removeItem(DEV_STORE_KEY);
  } catch {}
}

// The server's scope-bypass query pair (`key=value`, e.g. the SECURITY_BYPASS_*
// pair) is a real backdoor usable straight from curl/Postman — so it must NEVER
// be hardcoded in source, or it would leak via the production source map even
// though the executable code is dead. Instead the dev stores it once in their
// OWN browser localStorage (this machine only); the code reads it at runtime and
// never contains the literal value. Set it in the DEV panel, or by hand in the
// console as a `<key>=<value>` string (do not commit the actual value anywhere).
const DEV_BYPASS_KEY = "__dev_store_bypass";

/** The raw `key=value` bypass pair from localStorage, or null. Dev-only. */
export function getDevStoreBypass(): string | null {
  if (!import.meta.dev) return null;
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(DEV_BYPASS_KEY);
    const trimmed = raw?.trim();
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
}

/** Persist the bypass pair. No-op in prod / SSR. */
export function setDevStoreBypass(pair: string): void {
  if (!import.meta.dev) return;
  if (typeof localStorage === "undefined") return;
  const trimmed = pair?.trim();
  try {
    if (trimmed) localStorage.setItem(DEV_BYPASS_KEY, trimmed);
    else localStorage.removeItem(DEV_BYPASS_KEY);
  } catch {}
}

/**
 * DEV-ONLY query-string suffix that forces the server's scope bypass while a
 * store override is active. Append to any report/API URL built by a client that
 * doesn't route through useApi's interceptor (e.g. the axios instances in
 * stores/report2.ts). Returns `&<key>=<value>` from localStorage when
 * impersonating, else `''`. import.meta.dev-gated → empty (and tree-shaken) in
 * prod, and the literal secret is never present in source.
 */
export function devStoreBypassQuery(): string {
  if (!import.meta.dev) return "";
  if (!getDevStoreOverride()) return "";
  const bypass = getDevStoreBypass();
  return bypass ? `&${bypass}` : "";
}

/**
 * DEV-ONLY: apply the bypass pair onto a query OBJECT (for useApi's ofetch
 * interceptor, which builds params as an object rather than a URL string).
 * Parses `key=value` from localStorage; no literal secret in source.
 */
export function applyDevStoreBypassParam(query: Record<string, any>): void {
  if (!import.meta.dev) return;
  if (!getDevStoreOverride()) return;
  const bypass = getDevStoreBypass();
  if (!bypass) return;
  const eq = bypass.indexOf("=");
  if (eq <= 0) return;
  query[bypass.slice(0, eq)] = bypass.slice(eq + 1);
}

/** True when the stored bypass looks like a usable `key=value` pair. */
export function isDevStoreBypassPair(pair: string | null | undefined): boolean {
  const trimmed = pair?.trim() ?? "";
  const eq = trimmed.indexOf("=");
  return eq > 0 && eq < trimmed.length - 1;
}

let devAuthFailureShown = false;

/**
 * DEV-ONLY: call from any 401/403 handler that would redirect to /login.
 * Returns true when a store override is active — the caller must then NOT
 * redirect. Under the override the session is synthetic, so a 401/403 can only
 * mean the server rejected the bypass pair (missing or wrong); sending the dev
 * to /login cannot fix that, and logging in re-applies the override → loop.
 * Shows one alert pointing at the DEV panel instead.
 */
export function handleDevStoreAuthFailure(status: number | undefined, url = ""): boolean {
  if (!import.meta.dev) return false;
  const override = getDevStoreOverride();
  if (!override) return false;
  console.error(
    `[dev-store] ${status} while impersonating shop ${override} — bypass pair ` +
    `${getDevStoreBypass() ? "rejected by the server" : "is not set"}. ${url}`
  );
  if (!devAuthFailureShown && typeof window !== "undefined") {
    devAuthFailureShown = true;
    void import("sweetalert2").then(({ default: Swal }) =>
      Swal.fire({
        icon: "warning",
        title: `DEV override: server returned ${status ?? "an auth error"}`,
        html:
          `Viewing shop <b>${override}</b>, but the server rejected the request.<br>` +
          `The bypass pair (<code>key=value</code>) in the DEV panel (bottom-right) is ` +
          `${getDevStoreBypass() ? "wrong" : "empty"} — it must match the server's ` +
          `SECURITY_BYPASS_QUERY_KEY / _VALUE.`,
      })
    );
  }
  return true;
}

/** Clear the override (back to your real logged-in shop). No-op in prod / SSR. */
export function clearDevStoreOverride(): void {
  if (!import.meta.dev) return;
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(DEV_STORE_KEY);
  } catch {}
}
